'use client'

import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { Building2, Loader2, Package, Search, Users, Wrench, X } from 'lucide-react'
import { Button } from '@/core/components/button'
import { useShopProducts } from '@/features/shop/hooks/use-shop-products'
import { resolveShopImageUrl } from '@/features/shop/lib/shop-image'
import { cn } from '@/lib/utils'
import { useOutsidePress } from '@/shared/hooks/use-outside-press'
import { routes } from '@/shared/routes'
import { useSessionZipCode } from '@/shared/state/session-zip-provider'
import { leaveSite } from '@/shared/utilities/leave-site.utility'
import { useShopHandoff } from '@/features/auth/components/shop-link.client'
import { shopNextPath } from '@/shared/shop-site'
import { stripSearchHighlightMarkup } from '@/shared/utilities/search-highlight-markup'
import { useSearchIntentNavigation } from '../hooks/use-search-intent-navigation'
import { useSearchSuggestions } from '../hooks/use-search-suggestions'
import { addRecentSearch } from '../lib/recent-searches'
import { searchHref } from '../lib/search-navigation'
import { resolveSearchIntent } from '../models/search-intent.model'
import { relevanceScore } from '../models/search-ranking.model'
import { pickResultDisplay } from '../models/search-result.model'
import { MIN_SEARCH_QUERY_LENGTH } from '../search.constants'
import type { SearchEntityType, SearchResult } from '../types/search.types'

type OptionType = 'query' | 'hive' | 'business' | 'service' | 'product'

interface SearchOption {
  type: OptionType
  id: string
  label: string
  sublabel?: string
  imageUrl?: string | null
  href?: string
  /** Leaves this site (the Shop, ProFinder). */
  external?: boolean
}

export const SEARCH_PLACEHOLDER = 'Search a company, product, hive, or service'

export function searchOptionId(option: Pick<SearchOption, 'type' | 'id'>): string {
  return `${option.type}:${option.id}`
}

const ICONS: Record<OptionType, typeof Search> = {
  hive: Users,
  business: Building2,
  service: Wrench,
  product: Package,
  query: Search,
}
const LABELS: Partial<Record<OptionType, string>> = {
  hive: 'Hive',
  business: 'Business',
  service: 'Service',
  product: 'Product',
}
const SUGGESTED_ENTITIES: Partial<Record<SearchEntityType, OptionType>> = {
  hive: 'hive',
  business: 'business',
  service_funnel: 'service',
}
const isAbsolute = (href: string) => /^https?:\/\//i.test(href)

function OptionImage({ src, icon: Icon }: { src?: string | null; icon: typeof Search }) {
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    setFailed(false)
  }, [src])
  return src && !failed ? (
    // Suggestion thumbnails come from three hosts (ours, the Shop proxy, business
    // logos); the optimizer allowlist can't cover them, so this is a plain <img>.
    // eslint-disable-next-line @next/next/no-img-element
    <img src={src} alt="" onError={() => setFailed(true)} className="h-8 w-8 shrink-0 rounded-md object-cover" />
  ) : (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary">
      <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
    </span>
  )
}

interface UniversalSearchInputProps {
  initialValue?: string
  /** Logged-out surfaces never suggest people (the people route is JWT-gated). */
  isPublic: boolean
  variant?: 'hero' | 'compact'
  /** Replaces the default navigation to the public results page. */
  onSubmit?: (query: string) => void
}

/**
 * The one search box across HubHive, the Shop and ProFinder — the landing
 * hero and the results page's sticky bar. Port of the Lovable landing's
 * `UniversalSearchInput`. Typeahead rows come from two sources with their own
 * hooks: HubHive entities (hives, businesses, services) through the Go
 * backend, and Shop products through the Shop's public products API
 * (ADR-0132). Enter with nothing highlighted goes to the app the loaded
 * dropdown rows vote for — the Shop, ProFinder, the business directory, the
 * hives list — or to the all-results page when nothing is clear or nothing
 * has loaded yet (ADR-0133); the first row, "Search all", is the explicit way
 * to see everything.
 */
export function UniversalSearchInput({
  initialValue = '',
  isPublic,
  variant = 'hero',
  onSubmit,
}: UniversalSearchInputProps) {
  const router = useRouter()
  const { openShop } = useShopHandoff()
  const listboxId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const controlRef = useRef<HTMLDivElement>(null)
  const [value, setValue] = useState(initialValue)
  const [open, setOpen] = useState(false)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [composing, setComposing] = useState(false)
  const { sessionZip } = useSessionZipCode()
  const zip = sessionZip.zipCode || undefined
  const { goToQuery } = useSearchIntentNavigation()
  const typed = value.trim()

  // Both hooks own their debounce; they get the raw value.
  const hub = useSearchSuggestions(value, { includePeople: !isPublic })
  const shop = useShopProducts(value, { limit: 4, enabled: typed.length >= 1 })

  useEffect(() => {
    setValue(initialValue)
    setActiveId(null)
  }, [initialValue])

  useOutsidePress(
    controlRef,
    () => {
      setOpen(false)
      setActiveId(null)
    },
    { enabled: open }
  )

  const options = useMemo<SearchOption[]>(() => {
    if (!typed) return []
    const broad: SearchOption = { type: 'query', id: `all-${typed}`, label: `Search all for “${typed}”` }
    // Rows still showing for the previous keystroke are kept off the list, as
    // the Lovable input did, so an option never belongs to a stale query.
    const hubRows: SearchResult[] = hub.isPlaceholderData ? [] : (hub.data ?? [])
    const local: SearchOption[] = []
    for (const row of hubRows) {
      const type = SUGGESTED_ENTITIES[row.entity_type]
      if (!type) continue
      const display = pickResultDisplay(row)
      const label = stripSearchHighlightMarkup(display.title)
      const id = String(row.identifier ?? label)
      const href =
        type === 'service'
          ? routes.profinder.index({ q: label, fid: id, zip, autoSearch: true })
          : display.href
      local.push({
        type,
        id,
        label,
        sublabel: display.subtitle ? stripSearchHighlightMarkup(display.subtitle) : undefined,
        imageUrl: display.thumbnail,
        href,
        external: isAbsolute(href),
      })
      if (local.length >= 8) break
    }
    const products: SearchOption[] = (shop.isPlaceholderData ? [] : (shop.data ?? []))
      .slice(0, 3)
      .map((product) => ({
        type: 'product' as const,
        id: product.id,
        label: product.title,
        sublabel: product.business?.name ?? 'HubHive Shop',
        imageUrl: resolveShopImageUrl(product.images[0]) ?? product.business?.logoUrl,
        href: product.productUrl,
        external: true,
      }))
    const ranked = [...local, ...products].sort((a, b) => {
      const score = (option: SearchOption) =>
        option.type === 'service' ? 360 : relevanceScore(typed, { title: option.label, category: option.sublabel })
      return (
        score(b) - score(a) ||
        a.label.localeCompare(b.label) ||
        searchOptionId(a).localeCompare(searchOptionId(b))
      )
    })
    return [broad, ...ranked.slice(0, 8)]
  }, [typed, hub.data, hub.isPlaceholderData, shop.data, shop.isPlaceholderData, zip])

  const activeIndex = options.findIndex((option) => searchOptionId(option) === activeId)
  const loading = typed.length > 0 && (hub.isFetching || shop.isFetching)
  const hubError = !hub.isPlaceholderData && hub.isError
  const shopError = !shop.isPlaceholderData && shop.isError

  /** Everything, on the all-results page — the explicit "Search all" row. */
  const searchAll = (query = value) => {
    const trimmed = query.trim()
    if (trimmed.length < MIN_SEARCH_QUERY_LENGTH) return
    addRecentSearch(trimmed)
    setOpen(false)
    setActiveId(null)
    if (onSubmit) onSubmit(trimmed)
    else router.push(searchHref(trimmed))
  }

  /**
   * A submit with nothing picked: the dropdown rows already loaded for what
   * was typed vote on the app; rows still showing for an earlier keystroke,
   * or none at all, mean the default all-results page.
   */
  const submit = (query = value) => {
    const trimmed = query.trim()
    if (trimmed.length < MIN_SEARCH_QUERY_LENGTH) return
    setOpen(false)
    setActiveId(null)
    if (onSubmit) {
      addRecentSearch(trimmed)
      onSubmit(trimmed)
      return
    }
    const hubRows = hub.query === trimmed && !hub.isPlaceholderData && hub.data ? hub.data : []
    const shopProducts = shop.query === trimmed && !shop.isPlaceholderData && shop.data ? shop.data : []
    goToQuery(trimmed, resolveSearchIntent(trimmed, { hubRows, shopProducts }))
  }

  const activate = (option: SearchOption) => {
    if (option.type === 'query') return searchAll(value)
    addRecentSearch(option.label)
    setOpen(false)
    setActiveId(null)
    if (!option.href) return
    if (option.external) {
      // A Shop product opens signed in for a signed-in visitor (ADR-0150).
      if (shopNextPath(option.href) !== null) void openShop(option.href)
      else leaveSite(option.href)
    }
    else router.push(option.href)
  }

  const updateValue = (next: string) => {
    setValue(next)
    setActiveId(null)
    setOpen(Boolean(next.trim()))
  }

  return (
    <div
      ref={controlRef}
      className={cn('relative z-50 min-w-0 w-full', variant === 'hero' ? 'max-w-3xl' : 'max-w-2xl')}
    >
      <label htmlFor={`${listboxId}-input`} className="sr-only">
        Search HubHive
      </label>
      <div
        className={cn(
          'flex items-center gap-2 rounded-full border border-border bg-card shadow-sm focus-within:ring-2 focus-within:ring-primary/30',
          variant === 'hero' ? 'min-h-16 p-2 pl-5' : 'min-h-12 p-1.5 pl-4'
        )}
      >
        <Search
          className={cn('h-5 w-5 shrink-0 text-muted-foreground', variant === 'compact' && 'hidden sm:block')}
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          id={`${listboxId}-input`}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open && Boolean(typed)}
          aria-controls={listboxId}
          aria-activedescendant={activeId ? `${listboxId}-${encodeURIComponent(activeId)}` : undefined}
          value={value}
          onFocus={() => typed && setOpen(true)}
          onChange={(event) => updateValue(event.target.value)}
          onCompositionStart={() => setComposing(true)}
          onCompositionEnd={() => setComposing(false)}
          onKeyDown={(event) => {
            if (composing || event.nativeEvent.isComposing) return
            if (event.key === 'ArrowDown' && options.length) {
              event.preventDefault()
              setOpen(true)
              const next = activeIndex < 0 ? 0 : Math.min(activeIndex + 1, options.length - 1)
              setActiveId(searchOptionId(options[next]))
            } else if (event.key === 'ArrowUp' && options.length) {
              event.preventDefault()
              setOpen(true)
              const next = activeIndex <= 0 ? options.length - 1 : activeIndex - 1
              setActiveId(searchOptionId(options[next]))
            } else if (event.key === 'Enter') {
              event.preventDefault()
              if (activeId && activeIndex > 0 && options[activeIndex]) activate(options[activeIndex])
              else submit()
            } else if (event.key === 'Escape') {
              setOpen(false)
              setActiveId(null)
              inputRef.current?.blur()
            }
          }}
          placeholder={SEARCH_PLACEHOLDER}
          className="min-w-0 flex-1 bg-transparent text-base text-foreground outline-none placeholder:text-muted-foreground"
        />
        {value && (
          <Button type="button" variant="ghost" size="icon" aria-label="Clear search" onClick={() => updateValue('')}>
            <X className="h-4 w-4" />
          </Button>
        )}
        <Button
          type="button"
          onClick={() => submit()}
          className={cn('shrink-0 rounded-full', variant === 'hero' ? 'h-12 px-6' : 'h-9 px-3 sm:px-4')}
        >
          Search
        </Button>
      </div>

      {open && typed && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[min(420px,60vh)] overflow-y-auto rounded-2xl border border-border bg-popover p-1.5 text-popover-foreground shadow-lg"
        >
          {options.map((option, index) => {
            const Icon = ICONS[option.type]
            const optionId = searchOptionId(option)
            const highlighted = activeId === optionId
            return (
              <Button
                key={optionId}
                id={`${listboxId}-${encodeURIComponent(optionId)}`}
                role="option"
                aria-selected={highlighted}
                variant="ghost"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => activate(option)}
                className={cn(
                  'h-auto min-h-12 w-full justify-start rounded-xl px-3 py-2 text-left whitespace-normal',
                  highlighted && 'bg-secondary'
                )}
              >
                <OptionImage src={option.imageUrl} icon={Icon} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{option.label}</span>
                  {option.sublabel && (
                    <span className="block truncate text-xs text-muted-foreground">{option.sublabel}</span>
                  )}
                </span>
                {index > 0 && <span className="text-xs font-medium text-muted-foreground">{LABELS[option.type]}</span>}
              </Button>
            )
          })}
          {loading && (
            <div className="flex min-h-11 items-center gap-2 px-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading more results…
            </div>
          )}
          {!loading && options.length === 1 && !hubError && !shopError && (
            <p className="px-4 py-3 text-sm text-muted-foreground">
              No exact matches yet. Search all to see broader results.
            </p>
          )}
          {hubError && (
            <div className="flex items-center justify-between gap-3 px-4 py-2 text-sm text-destructive">
              <span>HubHive suggestions are unavailable.</span>
              <Button type="button" variant="ghost" size="sm" onClick={() => hub.refetch()}>
                Retry
              </Button>
            </div>
          )}
          {shopError && (
            <div className="flex items-center justify-between gap-3 px-4 py-2 text-sm text-destructive">
              <span>Shop suggestions are unavailable.</span>
              <Button type="button" variant="ghost" size="sm" onClick={() => shop.refetch()}>
                Retry
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
